import assert from 'assert';

import type { LibraryItem as SDKLibraryItem } from '@speechifyinc/multiplatform-sdk';

import { isCorsAllowed, wrapWithCorsProxy } from 'lib/speechify/adaptors/cors';
import type { MultiplatformSDKInstance } from 'modules/sdk/lib/sdk';
import { getParentFolderId } from 'modules/sdk/lib/utils/getParentFolderId';
import { parseAnalyticsProperties } from 'modules/sdk/lib/utils/parseAnalyticsProperties';
import { Nullable } from 'utils/types';

import { BaseListenableContent } from '../base';
import { ContentMetaType, ContentType } from '../content.types';

export class ItemListenableContent extends BaseListenableContent {
  private readonly sdk: MultiplatformSDKInstance;
  public readonly sdkItem: SDKLibraryItem.Content;
  public readonly parentFolderId: string;
  public readonly coverImageUrl: Nullable<string>;
  public readonly sourceUrl: Nullable<string> = null;
  public readonly originalSourceUrl: Nullable<string> = null;
  public readonly contentType: ContentType = ContentType.libraryItem;
  public readonly totalWords: number;
  public readonly analyticsProperties: Record<string, unknown>;
  public readonly metaType: ContentMetaType;

  constructor(sdk: MultiplatformSDKInstance, sdkItem: SDKLibraryItem.Content) {
    super();
    this.sdk = sdk;

    this.parentFolderId = getParentFolderId(sdkItem.parentFolderId);
    this.sdkItem = sdkItem;
    this.useItem.setState({ id: sdkItem.uri.id, parentFolderId: this.parentFolderId, title: sdkItem.title });
    this.coverImageUrl = sdkItem.coverImageUrl;

    this.totalWords = sdkItem.totalWords ?? 0;
    this.analyticsProperties = parseAnalyticsProperties(sdkItem);
    this.metaType = this._getContentMetaType(sdk, sdkItem);

    const sourceStoredUrl = sdkItem.sourceStoredUrl;
    const sourceUrl = sdkItem.sourceUrl;
    this.sourceUrl = sourceStoredUrl ?? sourceUrl;
    this.originalSourceUrl = sourceUrl;
  }

  private _getContentMetaType(sdk: MultiplatformSDKInstance, sdkItem: SDKLibraryItem.Content): ContentMetaType {
    const { ContentType } = sdk.sdkModule;

    switch (sdkItem.contentType) {
      case ContentType.PDF:
        return ContentMetaType.PDF;

      case ContentType.DOCX:
        return ContentMetaType.DOCX;

      case ContentType.EPUB:
        return ContentMetaType.EPUB;

      case ContentType.HTML:
        return ContentMetaType.HTML;

      case ContentType.SCAN:
        return ContentMetaType.SCAN;

      case ContentType.TXT:
        return ContentMetaType.TXT;

      default:
        return ContentMetaType.UNKNOWN;
    }
  }

  public get itemId(): string {
    return this.sdkItem.uri.id;
  }

  override subscribeToAutoUpdateUrlWhenItemIdReady(): () => void {
    // We do not need to update the url for items as we know the id there from the beginning
    // this is no-op
    return () => {};
  }

  content = async (): Promise<string> => {
    const url = this.sourceUrl;
    assert(url, 'PDFItemListenableContent: sourceUrl is null');
    if (isCorsAllowed(url)) return url;
    return wrapWithCorsProxy(url);
  };

  public get title(): string {
    return this.useItem.getState().title || this.sdkItem.title;
  }

  public get wordCount(): number {
    return this.totalWords;
  }

  public get importType() {
    return null;
  }
}
